import {
  isDevMode,
  app,
  fs,
  child,
  process,
  logDirectoryPath
} from "./electron-modules";
import path from "path";
import slash from "slash";

const engineMessageTypeJSON = "NODEJSJSONDATA";

class Executable {
  public exePath: string;
  public exeName: string;
  public running: boolean;
  public child;
  public developerKey: string;
  public spawnDetached: boolean;
  private logText = [];
  private showStatus = true;

  constructor(exePath, exeName) {
    this.exePath = exePath;
    this.exeName = exeName;
    this.running = false;
    this.child = null;
    this.spawnDetached = false;
  }

  get path() {
    let basePath;
    let appPath = slash(app.getAppPath());
    let executablePath;
    if (!isDevMode) {
      // calculate path using root + folder and name
      basePath = path.dirname(appPath);
      basePath = path.dirname(basePath);
      basePath = path.dirname(basePath);
      executablePath = path.join(basePath, this.exePath, this.exeName);
    } else {
      // grab full path from config file
      const contents = fs.readFileSync(`${appPath}/develop.env`);
      const env = JSON.parse(contents);
      executablePath = env[this.developerKey];
    }

    return executablePath;
  }

  statusMessage(message) {
    if (!this.showStatus) {
      return;
    }

    console.log(`[pid = ${this.child.pid}]: ${message}`);
  }

  parseCommandsList(commands) {
    // Convert objects to modified json
    for (let i=0;i<commands.length;i++) {
      if (typeof commands[i] === 'object') {
        commands[i] = JSON.stringify(commands[i])
          .replace(/"/g, "'");
      }
    }

    return commands;
  }

  copyableCommands(commands) {
    // Commands are slightly modified so they can be copy/pasted for test reasons
    return [...commands].map((text, index) => {
      return ((index % 2) === 0)
        ? text
        : `"${text}"`;
    }).join(" ");
  }

  run(opts = {}) {
    if (this.running) {
      console.log("Cannot launch multiple instances");
      return;
    }

    const defaults = {
      parseLineData: true,
      parseJSON: true,
      showStatus: true,
      saveLogs: false,
      command: null,
      onClose: null,
      onData: null,
      onLine: null
    };

    let options = {...defaults, ...opts};

    this.running = true;
    this.showStatus = options.showStatus;

    let commands = this.parseCommandsList(options.command);
    let executablePath = this.path;

    let cwd = path.dirname(executablePath);
    this.child = child.spawn(executablePath, commands, {
      cwd: cwd,
      env: {
        ...process.env,
        PYTHONUNBUFFERED: true,
        NODEJS: true
      },
      detached: this.spawnDetached
    });

    if (!this.child.pid) {
      console.error(`Error launching ${this.exeName} exe`);
      if (options.onClose) {
        options.onClose();
      }

      return;
    }

    let copyPasteCommands = this.copyableCommands(commands);
    this.statusMessage(`${this.path} ${copyPasteCommands}`);

    this.child.on('close', () => {
      this.statusMessage(`close`);

      if (options.saveLogs) {
        let commandIndex = commands.indexOf("-p") + 1;
        if (commandIndex > 0) {
          let commandName = commands[commandIndex];
          saveLogs(commandName, this.logText);
        }
      }
      this.running = false;
      this.child = null;

      if (options.onClose) {
        options.onClose();
      }
    });

    if (options.onData) {
      this.child.stderr.on('data', (data) => {
        options.onData(data, engineMessageTypeJSON);
      });

      this.child.stdout.on('data', (data) => {
        options.onData(data, engineMessageTypeJSON);
      });
    }

    if (options.onLine || options.saveLogs) {
      this.child.stderr.on('data', (data) => {
        this.parseLines(data, options);
      });

      this.child.stdout.on('data', (data) => {
        this.parseLines(data, options);
      });
    }

    return this.child;
  }

  /** @private */
  parseLines(data, opts) {
    let callback = opts.onLine ? opts.onLine : () => {};
    let parseLineData = opts.parseLineData;

    let dataString = data.toString();
    let lines = dataString.split("\n");

    lines.forEach(line => {
      line = line.trim();
      if (line === "") {
        return;
      }

      if (!parseLineData) {
        // Execute callback with raw line response
        callback(line);
      }

      // Parse our response and only keep engine json response
      let split = line.split(":");
      let messageType = split[0].trim();
      let messageData = split.slice(1).join(":").trim();

      if (messageType === engineMessageTypeJSON) {
        try {
          if (opts.parseJSON) {
            let jsonData = JSON.parse(messageData);
            callback(jsonData);
          } else {
            callback(messageData);
          }
        } catch {
          callback(null);
        }
      } else {
        // NOTE: can be removed once everything uses engineMessageTypeJSON
        if (!messageType.startsWith("NODEJS")) {
          this.logText.push(line);
        }
      }
    });
  }

  destroy() {
    if (this.running === false) {
      return;
    }

    this.running = false;

    // Kill original process id
    this.statusMessage(`force kill process`);
    process.kill(this.child.pid);

    try {
      let text = `wmic process get processid,parentprocessid,executablepath|find "${this.child.pid}"`;
      let stdout = child.execSync(text);
      let lines = stdout.toString().split("\n");

      let subprocessToKill = [];
      lines.forEach(line => {
        line = line.replace(/\s\s+/g, ' ');
        line = line.replace("\n", "");
        if (line.length === 0) {
          return;
        }

        line = line.split(" ");

        let rootProcessID = this.child.pid;
        let parentProcessID = parseInt(line.slice(-3)[0]);
        let processID = parseInt(line.slice(-2)[0]);
        line.splice(-3);

        // Wrong process (wrong parent id)
        if (parentProcessID !== rootProcessID) {
          return;
        }

        // Wrong process (processID == rootProcessID)
        if (processID === rootProcessID) {
          return;
        }

        // Check launch path (engine.exe and known executables)
        let cmdPath = slash(line.join(" "));
        let engineSubprocess = cmdPath.indexOf(this.exeName) !== -1;
        let engineExecutable = cmdPath.indexOf("engine/executables") !== -1;

        if (engineSubprocess || engineExecutable) {
          // Add our sub-process to the list of ones to stop
          subprocessToKill.push(processID);
        }
      });

      // Kill all of the child processes that may have spawned
      subprocessToKill.forEach(subprocessID => {
        this.statusMessage(`kill sub-process ${subprocessID}`);
        process.kill(subprocessID);
      });
    } catch (err) {
      // Do nothing
    }
  }
}

export class PythonExecutable extends Executable {
  constructor() {
    super("exe-engine", "engine.exe");
    this.developerKey = "exe-python";
    window.onbeforeunload = () => {
      this.destroy();
    };
  }

  write(text) {
    if (!this.running) {
      return;
    }

    this.child.stdin.write(text);
  }
}

export class WorkflowExecutable extends Executable {
  constructor() {
    super("../", "SOLV3D engine.exe");
    this.developerKey = "exe-main";
    this.spawnDetached = true;
  }
}

const saveLogs = async (logPrefix, logText) => {
  const maxLogFiles = 50;

  await fs.promises.mkdir(logDirectoryPath)
    .catch(() => {});

  const dateString = new Date()
    .toISOString()
    .replaceAll(":", "-");

  const logPath = path.join(
    logDirectoryPath,
    `${logPrefix} ${dateString}.txt`
  );

  const logs = logText.join("\n");
  fs.promises.writeFile(logPath, logs);

  const filenames = await fs.promises.readdir(logDirectoryPath);
  if (filenames.length <= maxLogFiles) {
    return;
  }

  let oldest = new Date();
  let oldestFilePath;

  for (let filename of filenames) {
    const filePath = path.join(logDirectoryPath, filename);
    const stats = await fs.promises.stat(filePath);

    if (stats.birthtime < oldest) {
      oldest = stats.birthtime;
      oldestFilePath = filePath;
    }
  }

  await fs.promises.unlink(oldestFilePath)
    .catch(() => {});
};
